import { useState, useEffect, useMemo, useCallback } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import { Dropdownlist } from '@/components/Dropdownlist';
import { Tooltip } from '@/components/Tooltip';
import { getOfficerLabel } from '@/utils/auth';

export interface SelfInitiateNocOption {
  code: number;
  value: string;
  listIdx?: number;
}

export interface SelfInitiateNocEditData {
  nocCode: number;
  nocValue: string;
  isShownOnMobile: boolean;
  officerStatusLevel: string;
  secondaryNoc: number | null;
}

export interface AddSelfInitiateNocModalProps {
  open: boolean;
  availableNocs?: SelfInitiateNocOption[];
  allCompanyNocs?: SelfInitiateNocOption[];
  theme?: string;
  saving?: boolean;
  editData?: SelfInitiateNocEditData | null;
  onSave: (data: { nocCode: number; isShownOnMobile: boolean; officerStatusLevel: string; secondaryNoc: number | null }) => void;
  onCancel: () => void;
}

export function AddSelfInitiateNocModal({
  open,
  availableNocs = [],
  allCompanyNocs = [],
  theme,
  saving = false,
  editData = null,
  onSave,
  onCancel,
}: AddSelfInitiateNocModalProps) {
  const [selectedNocCode, setSelectedNocCode] = useState<number | null>(null);
  const [isShownOnMobile, setIsShownOnMobile] = useState(true);
  const [officerStatusLevel, setOfficerStatusLevel] = useState('ON_SCENE');
  const [secondaryNocCode, setSecondaryNocCode] = useState<number | null>(null);

  // Reset form when opening
  useEffect(() => {
    if (open) {
      if (editData) {
        setSelectedNocCode(editData.nocCode);
        setIsShownOnMobile(editData.isShownOnMobile);
        setOfficerStatusLevel(editData.officerStatusLevel || 'ON_SCENE');
        setSecondaryNocCode(editData.secondaryNoc);
      } else {
        setSelectedNocCode(null);
        setIsShownOnMobile(true);
        setOfficerStatusLevel('ON_SCENE');
        setSecondaryNocCode(null);
      }
    }
  }, [open, editData]);

  const nocItems = useMemo(() => {
    // Sort by listIdx to respect the user's configured order
    const sorted = availableNocs.slice().sort((a, b) => {
      const aIdx = a.listIdx ?? Number.MAX_SAFE_INTEGER;
      const bIdx = b.listIdx ?? Number.MAX_SAFE_INTEGER;
      return aIdx - bIdx;
    });
    return sorted.map((noc) => ({
      label: noc.value,
      value: noc.code,
    }));
  }, [availableNocs]);

  const officerLabel = useMemo(() => getOfficerLabel().toLowerCase(), []);

  const statusTooltip = useMemo(() =>
    `The ${officerLabel} status to automatically set when this button is clicked on the Self Initiate screen.<br><br>` +
    `<b>Dispatched</b> &ndash; ${officerLabel} is dispatched only.<br>` +
    `<b>Enroute</b> &ndash; ${officerLabel} is dispatched and enroute.<br>` +
    `<b>On Scene</b> &ndash; ${officerLabel} is dispatched, enroute, and on scene.<br>` +
    `<b>Cleared</b> &ndash; ${officerLabel} is dispatched, enroute, on scene, and cleared.`
  , [officerLabel]);

  const officerStatusLevelOptions = useMemo(() => [
    { label: 'Dispatched', value: 'DISPATCHED' },
    { label: 'Enroute', value: 'ENROUTE' },
    { label: 'On Scene', value: 'ON_SCENE' },
    { label: 'Cleared', value: 'CLEARED' },
  ], []);

  const secondaryNocItems = useMemo(() => {
    const sorted = allCompanyNocs.slice().sort((a, b) => {
      const aIdx = a.listIdx ?? Number.MAX_SAFE_INTEGER;
      const bIdx = b.listIdx ?? Number.MAX_SAFE_INTEGER;
      return aIdx - bIdx;
    });
    return sorted.map((noc) => ({
      label: noc.value,
      value: noc.code,
    }));
  }, [allCompanyNocs]);

  const handleSave = useCallback(() => {
    if (selectedNocCode === null) {
      (window as any).App?.showPopupNotification?.('Nature of call required.', 'error');
      return;
    }

    onSave({
      nocCode: selectedNocCode,
      isShownOnMobile,
      officerStatusLevel,
      secondaryNoc: secondaryNocCode,
    });
  }, [selectedNocCode, isShownOnMobile, officerStatusLevel, secondaryNocCode, onSave]);

  return (
    <BaseFormModal
      open={open}
      title={editData ? "Edit Self Initiate NOC" : "Add Nature of Call"}
      theme={theme}
      dialogClass="kstc-dialog--form"
      saveText={saving ? 'Saving...' : (editData ? 'Save' : 'Add')}
      saveDisabled={saving}
      busy={saving}
      onSave={handleSave}
      onCancel={onCancel}
    >
      <div className="kstc-form-group">
        <label className="kstc-label">{editData ? 'Nature of Call' : 'Nature of Call to add to Self Initiate screen'}</label>
        {editData ? (
          <div className="kstc-form-static">{editData.nocValue}</div>
        ) : (
          <Dropdownlist
            value={selectedNocCode}
            items={nocItems}
            placeholder="Select Nature of Call..."
            sortItems={false}
            onChange={(val) => setSelectedNocCode(val as number | null)}
          />
        )}
      </div>

      <div className="kstc-form-group">
        <label className="kstc-checkbox-label">
          <input
            type="checkbox"
            className="kstc-checkbox"
            checked={isShownOnMobile}
            onChange={(e) => setIsShownOnMobile(e.target.checked)}
          />
          <span>Shown on small screens?</span>
        </label>
      </div>

      <div className="kstc-form-group">
        <label className="kstc-label">Status <Tooltip content={statusTooltip} position="right" /></label>
        <Dropdownlist
          value={officerStatusLevel}
          items={officerStatusLevelOptions}
          placeholder="Select status level..."
          sortItems={false}
          searchable={false}
          onChange={(val) => setOfficerStatusLevel((val as string) || 'ON_SCENE')}
        />
      </div>

      <div className="kstc-form-group">
        <label className="kstc-label">Secondary NOC (optional) <Tooltip content="An additional Nature of Call that will be automatically added to the Call for Service when this button is clicked. Useful for tracking groups of Calls in Analytics Reports." position="right" /></label>
        <Dropdownlist
          value={secondaryNocCode}
          items={secondaryNocItems}
          placeholder="None"
          sortItems={false}
          clearable={true}
          onChange={(val) => setSecondaryNocCode(val as number | null)}
        />
      </div>
    </BaseFormModal>
  );
}

(window as any).AddSelfInitiateNocModalReact = AddSelfInitiateNocModal;
